import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { createPublicApi } from '../../services/api';
import toast from 'react-hot-toast';


// ── Likes (LocalStorage) — scoped per restaurant slug ────────
const getLikesKey = (slug) => `cafe_liked_items_${slug || 'default'}`;

const getLikes = (slug) => {
  try { return JSON.parse(localStorage.getItem(getLikesKey(slug)) || '[]'); }
  catch { return []; }
};

const useLike = (itemId, slug) => {
  const [liked, setLiked] = useState(() => getLikes(slug).includes(itemId));

  const toggle = (e) => {
    e.stopPropagation();
    const current = getLikes(slug);
    const updated = current.includes(itemId)
      ? current.filter(id => id !== itemId)
      : [...current, itemId];
    localStorage.setItem(getLikesKey(slug), JSON.stringify(updated));
    setLiked(!current.includes(itemId));
    window.dispatchEvent(new CustomEvent('likes-updated', { detail: { slug } }));
  };

  return { liked, toggle };
};
// ─────────────────────────────────────────────────────────────

const PLACEHOLDER = 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=800&q=80';
const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const imgSrc = (url) => url ? (url.startsWith('http') ? url : `${BASE}${url}`) : PLACEHOLDER;

const C = {
  accent:'#b85c38', accentD:'#9a4928', accentL:'#fff4ee', accentB:'#fde0cc',
  text1:'#1c1512', text2:'#3d2010', text3:'#6b4c35', text4:'#a07858',
  border:'#f0e4d8', card:'#ffffff', bg:'#fdf8f4', star:'#f59e0b', starE:'#e8e0d8',
};

const Stars = ({ rating=0, size=14, interactive=false, onRate }) => {
  const [hover, setHover] = useState(0);
  const display = interactive ? (hover || rating) : rating;
  return (
    <div style={{ display:'flex', gap:2 }}>
      {[1,2,3,4,5].map(s => (
        <span key={s}
          onClick={() => interactive && onRate && onRate(s)}
          onMouseEnter={() => interactive && setHover(s)}
          onMouseLeave={() => interactive && setHover(0)}
          style={{ fontSize:size, color: s<=display ? C.star : C.starE, cursor: interactive?'pointer':'default', transition:'color 0.1s', lineHeight:1 }}>★</span>
      ))}
    </div>
  );
};

const ReviewForm = ({ itemId, slug, onSubmitted }) => {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const labels = ['','Very Bad','Not Good','Okay','Good','Excellent!'];

  const submit = async () => {
    if (!name.trim()) return toast.error('Please enter your name');
    if (!rating) return toast.error('Please select a rating');
    setLoading(true);
    try {
      const pubApi = createPublicApi(slug);
      await pubApi.reviews.create(itemId, { reviewer_name: name, rating, comment });
      toast.success('Review submitted successfully!');
      setName(''); setRating(0); setComment('');
      onSubmitted();
    } catch { toast.error('Failed to submit review'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ background:C.accentL, borderRadius:16, padding:'18px 20px', border:`1px solid ${C.accentB}` }}>
      <p style={{ fontSize:14, fontWeight:700, color:C.text2, margin:'0 0 14px', fontFamily:'"Cormorant Garamond",serif' }}>Write Your Review</p>
      <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name"
        style={{ width:'100%', padding:'10px 14px', borderRadius:10, fontSize:13, border:`1.5px solid ${C.border}`, background:C.card, color:C.text1, fontFamily:'inherit', outline:'none', boxSizing:'border-box', marginBottom:10 }} />
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <Stars rating={rating} size={26} interactive onRate={setRating} />
        <span style={{ fontSize:12, color:C.text4 }}>{labels[rating] || 'Select rating'}</span>
      </div>
      <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder="What did you like? (optional)" rows={3}
        style={{ width:'100%', padding:'10px 14px', borderRadius:10, fontSize:13, border:`1.5px solid ${C.border}`, background:C.card, color:C.text1, fontFamily:'inherit', outline:'none', boxSizing:'border-box', resize:'none', marginBottom:12 }} />
      <button onClick={submit} disabled={loading}
        style={{ width:'100%', padding:'11px', borderRadius:10, border:'none', background: loading?'#ccc':`linear-gradient(135deg,${C.accent},${C.accentD})`, color:'white', fontSize:13, fontWeight:700, fontFamily:'inherit', cursor: loading?'not-allowed':'pointer' }}>
        {loading ? 'Submitting...' : 'Submit Review'}
      </button>
    </div>
  );
};

const ReviewsList = ({ reviews, stats }) => {
  if (!stats || stats.total === 0) return (
    <div style={{ textAlign:'center', padding:'24px 0', color:C.text4, fontSize:13 }}>No reviews yet. Be the first to write one!</div>
  );
  const keys = ['','one','two','three','four','five'];
  return (
    <div>
      <div style={{ display:'flex', gap:20, alignItems:'center', marginBottom:16, padding:'14px 16px', background:C.bg, borderRadius:12, border:`1px solid ${C.border}` }}>
        <div style={{ textAlign:'center', flexShrink:0 }}>
          <div style={{ fontSize:32, fontWeight:800, color:C.accent, fontFamily:'"Cormorant Garamond",serif', lineHeight:1 }}>{stats.average||'0.0'}</div>
          <Stars rating={parseFloat(stats.average||0)} size={13} />
          <div style={{ fontSize:11, color:C.text4, marginTop:3 }}>{stats.total} reviews</div>
        </div>
        <div style={{ flex:1 }}>
          {[5,4,3,2,1].map(n => {
            const count = stats[keys[n]]||0;
            const pct = stats.total>0 ? (count/stats.total)*100 : 0;
            return (
              <div key={n} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ fontSize:11, color:C.text4, width:8, textAlign:'right' }}>{n}</span>
                <span style={{ fontSize:11, color:C.star }}>★</span>
                <div style={{ flex:1, height:6, background:C.starE, borderRadius:99, overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:C.star, borderRadius:99, transition:'width 0.6s' }} />
                </div>
                <span style={{ fontSize:11, color:C.text4, width:16 }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10, maxHeight:260, overflowY:'auto' }}>
        {reviews.map(r => (
          <div key={r.id} style={{ padding:'12px 14px', background:C.card, borderRadius:12, border:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
              <div>
                <span style={{ fontSize:13, fontWeight:700, color:C.text1 }}>{r.reviewer_name}</span>
                <div style={{ marginTop:3 }}><Stars rating={r.rating} size={12} /></div>
              </div>
              <span style={{ fontSize:11, color:C.text4 }}>{new Date(r.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>
            </div>
            {r.comment && <p style={{ fontSize:13, color:C.text3, margin:0, lineHeight:1.6 }}>{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductModal = ({ item, onClose, slug }) => {
  const { addItem, items, updateQty } = useCart();
  const inCart = items.find(i => i.id === item.id);
  const [imgErr, setImgErr] = useState(false);
  const [tab, setTab] = useState('details');
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingR, setLoadingR] = useState(false);

  const loadReviews = async () => {
    setLoadingR(true);
    try {
      const pubApi = createPublicApi(slug);
      const res = await pubApi.reviews.getAll(item.id);
      setReviews(res.data.data.reviews||[]);
      setStats(res.data.data.stats||null);
    } catch {} finally { setLoadingR(false); }
  };

  useEffect(() => { loadReviews(); }, [item.id]);

  const handleAdd = () => {
    addItem({ id:item.id, name:item.name, price:parseFloat(item.price), image_url:item.image_url, slug });
    toast.success(`${item.name} added!`);
  };

  const avg = parseFloat(stats?.average||0);
  const total = parseInt(stats?.total||0);

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(10,5,0,0.75)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <motion.div initial={{opacity:0,scale:0.92,y:30}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.92,y:30}}
        transition={{type:'spring',damping:26,stiffness:300}} onClick={e=>e.stopPropagation()}
        style={{ background:C.card, borderRadius:24, width:'100%', maxWidth:480, maxHeight:'90vh', overflow:'hidden', boxShadow:'0 40px 80px rgba(0,0,0,0.45)', display:'flex', flexDirection:'column' }}>

        <div style={{ position:'relative', width:'100%', height:240, background:C.bg, overflow:'hidden', flexShrink:0 }}>
          <img src={imgErr?PLACEHOLDER:imgSrc(item.image_url)} alt={item.name} onError={()=>setImgErr(true)}
            style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(26,15,0,0.55) 0%, transparent 55%)' }}/>
          <div style={{ position:'absolute', top:14, left:14, display:'flex', gap:6 }}>
            {item.is_special  && <span style={{ padding:'4px 12px', borderRadius:99, background:C.accent,  color:'white', fontSize:11, fontWeight:700 }}>⭐ Special</span>}
            {item.is_featured && <span style={{ padding:'4px 12px', borderRadius:99, background:'#7c3aed', color:'white', fontSize:11, fontWeight:700 }}>🏆 Featured</span>}
          </div>
          {total>0 && (
            <div style={{ position:'absolute', bottom:14, left:14, display:'flex', alignItems:'center', gap:6, background:'rgba(0,0,0,0.55)', borderRadius:99, padding:'5px 12px' }}>
              <Stars rating={avg} size={13} />
              <span style={{ fontSize:12, color:'white', fontWeight:700 }}>{avg.toFixed(1)}</span>
              <span style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>({total})</span>
            </div>
          )}
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, width:34, height:34, borderRadius:'50%', background:'rgba(255,255,255,0.9)', border:'none', cursor:'pointer', fontSize:16, color:C.text2, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>

        <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          {['details','reviews'].map(t => (
            <button key={t} onClick={()=>setTab(t)}
              style={{ flex:1, padding:'12px', border:'none', background:'transparent', borderBottom:`2.5px solid ${tab===t?C.accent:'transparent'}`, color:tab===t?C.accent:C.text4, fontWeight:tab===t?700:500, fontSize:13, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}>
              {t==='reviews' ? `Reviews${total>0?` (${total})`:''}` : 'Details'}
            </button>
          ))}
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 22px 24px' }}>
          {tab==='details' && (
            <>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:8 }}>
                <h2 style={{ fontSize:22, fontWeight:700, color:C.text1, margin:0, fontFamily:'"Cormorant Garamond",serif', lineHeight:1.2, flex:1 }}>{item.name}</h2>
                <p style={{ fontSize:24, fontWeight:800, color:C.accent, margin:0, flexShrink:0, fontFamily:'"Cormorant Garamond",serif' }}>₹{parseFloat(item.price).toFixed(0)}</p>
              </div>
              {total>0 && (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, cursor:'pointer' }} onClick={()=>setTab('reviews')}>
                  <Stars rating={avg} size={14} />
                  <span style={{ fontSize:13, color:C.text4 }}>{avg.toFixed(1)} · {total} review{total!==1?'s':''}</span>
                  <span style={{ fontSize:12, color:C.accent }}>→ View</span>
                </div>
              )}
              <p style={{ fontSize:14, color:C.text4, lineHeight:1.7, margin:'0 0 18px' }}>{item.description||'A delightful treat crafted with love from our kitchen.'}</p>
              <div style={{ height:1, background:C.border, margin:'0 0 18px' }}/>
              {item.is_available ? (
                inCart ? (
                  <div>
                    <p style={{ fontSize:11, fontWeight:700, color:C.text4, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:10 }}>In Your Cart</p>
                    <div style={{ display:'flex', alignItems:'center', background:C.bg, borderRadius:14, padding:4, border:`1px solid ${C.border}` }}>
                      <button onClick={()=>updateQty(item.id,inCart.quantity-1)} style={{ flex:1, height:44, borderRadius:10, border:'none', background:C.card, cursor:'pointer', fontSize:22, fontWeight:700, color:C.accent }}>−</button>
                      <span style={{ flex:1, textAlign:'center', fontSize:18, fontWeight:800, color:C.text1 }}>{inCart.quantity}</span>
                      <button onClick={()=>updateQty(item.id,inCart.quantity+1)} style={{ flex:1, height:44, borderRadius:10, border:'none', background:`linear-gradient(135deg,${C.accent},${C.accentD})`, cursor:'pointer', fontSize:22, fontWeight:700, color:'white' }}>+</button>
                    </div>
                    <p style={{ fontSize:13, color:C.text4, textAlign:'center', marginTop:10 }}>Subtotal: <strong style={{color:C.accent}}>₹{(parseFloat(item.price)*inCart.quantity).toFixed(0)}</strong></p>
                  </div>
                ) : (
                  <motion.button whileTap={{scale:0.97}} onClick={handleAdd}
                    style={{ width:'100%', padding:'14px', borderRadius:14, border:'none', background:`linear-gradient(135deg,${C.accent},${C.accentD})`, color:'white', fontSize:15, fontWeight:700, fontFamily:'inherit', cursor:'pointer', boxShadow:`0 4px 16px rgba(184,92,56,0.35)` }}>
                    🛒 Add to Cart — ₹{parseFloat(item.price).toFixed(0)}
                  </motion.button>
                )
              ) : (
                <div style={{ padding:14, borderRadius:14, textAlign:'center', background:C.bg, border:`1px solid ${C.border}`, color:C.text4, fontSize:14, fontWeight:600 }}>Currently Unavailable</div>
              )}
            </>
          )}
          {tab==='reviews' && (
            loadingR ? <div style={{ textAlign:'center', padding:'30px 0', color:C.text4 }}>Loading...</div> : (
              <>
                <ReviewsList reviews={reviews} stats={stats} />
                <div style={{ height:1, background:C.border, margin:'20px 0' }}/>
                <ReviewForm itemId={item.id} slug={slug} onSubmitted={loadReviews} />
              </>
            )
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const MenuItemCard = ({ item, showAddBtn=true }) => {
  const { addItem, items, updateQty } = useCart();
  const { slug } = useParams();
  const { liked, toggle: toggleLike } = useLike(item.id, slug);
  const [showModal, setShowModal] = useState(false);
  const [imgErr,    setImgErr]    = useState(false);
  const [avgRating, setAvgRating] = useState(0);
  const [totalRev,  setTotalRev]  = useState(0);
  const inCart = items.find(i => i.id === item.id);

  useEffect(() => {
    const pubApi = createPublicApi(slug);
    pubApi.reviews.getAll(item.id).then(res => {
      const s = res.data.data.stats;
      if (s && s.total > 0) { setAvgRating(parseFloat(s.average)); setTotalRev(parseInt(s.total)); }
    }).catch(() => {});
  }, [item.id, slug]);

  const handleAdd = (e) => {
    e.stopPropagation();
    addItem({ id:item.id, name:item.name, price:parseFloat(item.price), image_url:item.image_url, slug });
    toast.success(`${item.name} added!`);
  };

  return (
    <>
      <motion.div
        initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
        whileHover={{ y:-3, boxShadow:'0 8px 28px rgba(0,0,0,0.10)' }}
        transition={{ duration:0.2 }}
        onClick={() => setShowModal(true)}
        style={{ background:'#fff', borderRadius:14, overflow:'hidden', cursor:'pointer',
          border:'1px solid #ede8e3', boxShadow:'0 2px 8px rgba(0,0,0,0.05)',
          display:'flex', flexDirection:'column', height:'100%' }}>

        {/* Image */}
        <div style={{ position:'relative', width:'100%', height:170, background:'#f5ede4', overflow:'hidden', flexShrink:0 }}>
          <img src={imgErr ? PLACEHOLDER : imgSrc(item.image_url)} alt={item.name}
            onError={() => setImgErr(true)}
            style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.4s' }}
            onMouseEnter={e => e.target.style.transform='scale(1.05)'}
            onMouseLeave={e => e.target.style.transform='scale(1)'} />
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top,rgba(0,0,0,0.28) 0%,transparent 55%)', pointerEvents:'none' }}/>

          {/* Badges top-left */}
          <div style={{ position:'absolute', top:9, left:9, display:'flex', gap:4 }}>
            {item.is_special  && <span style={{ fontSize:10, fontWeight:700, padding:'3px 7px', borderRadius:6, background:'#c96428', color:'#fff' }}>🔥 Special</span>}
            {item.is_featured && <span style={{ fontSize:10, fontWeight:700, padding:'3px 7px', borderRadius:6, background:'#7c3aed', color:'#fff' }}>⭐ Featured</span>}
          </div>

          {/* Heart top-right */}
          <motion.button whileTap={{ scale:0.75 }} onClick={toggleLike}
            style={{ position:'absolute', top:8, right:8, width:32, height:32, borderRadius:8,
              background: liked ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.88)',
              border: `1.5px solid ${liked ? '#fca5a5' : 'rgba(255,255,255,0.6)'}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor:'pointer', backdropFilter:'blur(4px)', zIndex:2 }}>
            <svg width="15" height="15" viewBox="0 0 24 24"
              fill={liked ? '#ef4444' : 'none'}
              stroke={liked ? '#ef4444' : '#6b4c35'} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </motion.button>

          {/* Veg dot */}
          <div style={{ position:'absolute', bottom:9, left:9, width:18, height:18, border:'2px solid #16a34a', borderRadius:3, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#16a34a' }}/>
          </div>

          {/* Unavailable overlay */}
          {!item.is_available && (
            <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontSize:11, fontWeight:700, background:'rgba(0,0,0,0.35)', padding:'4px 12px', borderRadius:6 }}>Unavailable</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding:'11px 13px 13px', display:'flex', flexDirection:'column', flex:1 }}>

          {/* Name + Price */}
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6, marginBottom:4 }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:'#1a0f00', margin:0, lineHeight:1.3,
              display:'-webkit-box', WebkitLineClamp:1, WebkitBoxOrient:'vertical', overflow:'hidden', flex:1 }}>
              {item.name}
            </h3>
            <span style={{ fontSize:15, fontWeight:800, color:'#c96428', flexShrink:0 }}>
              ₹{parseFloat(item.price).toFixed(0)}
            </span>
          </div>

          {/* Description */}
          <p style={{ fontSize:11, color:'#8b6347', lineHeight:1.5, margin:'0 0 8px',
            display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', flex:1 }}>
            {item.description || 'A delightful treat from our kitchen.'}
          </p>

          {/* Rating + Category */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            {totalRev > 0 ? (
              <div style={{ display:'flex', alignItems:'center', gap:3, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6, padding:'2px 7px' }}>
                <span style={{ fontSize:11, color:'#16a34a' }}>★</span>
                <span style={{ fontSize:12, fontWeight:700, color:'#15803d' }}>{avgRating.toFixed(1)}</span>
                <span style={{ fontSize:10, color:'#6b7280' }}>({totalRev})</span>
              </div>
            ) : (
              <span style={{ fontSize:10, color:'#c0a080' }}>No reviews</span>
            )}
            <span style={{ fontSize:10, color:'#8b6347', background:'#f5ede4', borderRadius:6, padding:'2px 7px' }}>
              {item.category_name || 'Other'}
            </span>
          </div>

          {/* Add button */}
          {showAddBtn && item.is_available && (
            inCart ? (
              <div onClick={e => e.stopPropagation()}
                style={{ display:'flex', alignItems:'center', background:'#fff4ee', borderRadius:8, padding:3, border:'1px solid #fdd8c0' }}>
                <button onClick={e => { e.stopPropagation(); updateQty(item.id, inCart.quantity - 1); }}
                  style={{ flex:1, height:30, borderRadius:6, border:'none', background:'#fff', cursor:'pointer', fontSize:18, fontWeight:700, color:'#c96428' }}>−</button>
                <span style={{ flex:1, textAlign:'center', fontSize:14, fontWeight:800, color:'#1a0f00' }}>{inCart.quantity}</span>
                <button onClick={e => { e.stopPropagation(); updateQty(item.id, inCart.quantity + 1); }}
                  style={{ flex:1, height:30, borderRadius:6, border:'none', background:'#c96428', cursor:'pointer', fontSize:18, fontWeight:700, color:'#fff' }}>+</button>
              </div>
            ) : (
              <motion.button whileTap={{ scale:0.95 }} onClick={handleAdd}
                style={{ width:'100%', padding:'9px 0', borderRadius:8, border:'none',
                  background:'#c96428', color:'#fff', fontSize:13, fontWeight:700,
                  cursor:'pointer', fontFamily:'inherit', letterSpacing:'0.3px' }}>
                + Add to Cart
              </motion.button>
            )
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {showModal && <ProductModal item={item} slug={slug} onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </>
  );
};

export default MenuItemCard;
